<template>
  <section class="py-10 bg-white">
    <div class="max-w-6xl mx-auto px-6">
      <!-- 헤더 -->
      <div class="mb-10">
        <h2 class="text-[32px] font-extrabold text-slate-900 tracking-tight mb-2">지역별 분양 현장</h2>
        <p class="text-[16px] text-slate-500 font-medium">초기 자금 부담 없는 알짜 현장들을 모았습니다</p>
      </div>

      <!-- 권역 탭 -->
      <div class="flex gap-2 mb-6 overflow-x-auto scrollbar-hide pb-1">
        <button
          v-for="area in areas"
          :key="area.id"
          @click="selectArea(area.id)"
          :class="[
            'flex-shrink-0 px-5 py-2.5 rounded-xl text-[15px] font-semibold transition-all',
            selectedArea === area.id
              ? 'bg-slate-900 text-white'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700'
          ]"
        >
          {{ area.label }}
        </button>
      </div>

      <!-- 세부 지역 칩 -->
      <div class="flex gap-2 mb-8 overflow-x-auto scrollbar-hide pb-1">
        <button
          v-for="region in currentAreaRegions"
          :key="region"
          @click="selectRegion(region)"
          :class="[
            'flex-shrink-0 px-4 py-2 rounded-lg text-[14px] font-medium transition-all border',
            selectedRegion === region
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-600'
          ]"
        >
          {{ region }}
        </button>
      </div>

      <!-- 슬라이더 -->
      <div class="relative">
        <!-- 네비게이션 버튼 -->
        <button
          v-show="canScrollLeft"
          @click="scroll('left')"
          class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white border border-slate-200 rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors"
        >
          <svg class="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>

        <button
          v-show="canScrollRight"
          @click="scroll('right')"
          class="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white border border-slate-200 rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors"
        >
          <svg class="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>

        <!-- 스크롤 영역 -->
        <div
          ref="scrollContainer"
          @scroll="checkScroll"
          class="flex gap-4 overflow-x-auto pb-2 scrollbar-hide"
        >
          <!-- 로딩 -->
          <div v-if="isLoading" class="flex-1 py-12 text-center text-slate-400 text-[14px]">
            로딩중...
          </div>

          <!-- 빈 상태 -->
          <div v-else-if="filteredItems.length === 0" class="flex-1 py-12 text-center text-slate-400 text-[14px]">
            해당 지역에 등록된 현장이 없습니다.
          </div>

          <!-- 현장 카드 -->
          <div
            v-else
            v-for="item in filteredItems"
            :key="item.id"
            @click="goToDetail(item.id)"
            class="flex-shrink-0 w-[300px] bg-white rounded-2xl p-5 shadow-sm border border-slate-100 cursor-pointer hover:shadow-md hover:border-blue-200 transition-all"
          >
            <!-- 상단: 지역 + 타입 -->
            <div class="flex items-center justify-start mb-3">
              <span class="text-[13px] text-slate-400 font-medium">
                {{ item.location }} · {{ item.type }}
              </span>
            </div>

            <!-- 현장명 -->
            <h3 class="text-[17px] font-bold text-slate-900 mb-4 line-clamp-2 leading-snug">
              {{ item.title }}
            </h3>

            <!-- 하단: 계약금 정보 -->
            <div class="pt-3 border-t border-slate-100">
              <p class="text-[12px] text-slate-400 mb-1">필요한 초기자금</p>
              <p class="text-[18px] font-extrabold text-blue-600">{{ item.downPaymentHighlight }}</p>
              <p v-if="item.downPaymentSub" class="text-[13px] text-slate-500 mt-0.5">{{ item.downPaymentSub }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 하단 버튼 -->
      <div class="text-center mt-8">
        <router-link
          :to="{ path: '/properties', query: { region: selectedRegion } }"
          class="inline-flex items-center gap-1 text-[15px] font-semibold text-slate-500 hover:text-slate-900 hover:bg-slate-100 px-5 py-2.5 rounded-xl transition-all"
        >
          {{ selectedRegion }} 현장 전체보기
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useProperty } from '@/composables/admin/useProperty'

export default {
  name: 'LocationSection',
  setup() {
    const router = useRouter()
    const { fetchProperties, properties } = useProperty()

    const scrollContainer = ref(null)
    const canScrollLeft = ref(false)
    const canScrollRight = ref(true)
    const isLoading = ref(true)
    const selectedArea = ref('capital')
    const selectedRegion = ref('서울')

    // 5개 권역 + 세부 지역 (PropertyListView와 동일한 지역값 사용)
    const areas = [
      { id: 'capital', label: '수도권', regions: ['서울', '경기', '인천'] },
      { id: 'chungcheong', label: '충청권', regions: ['대전', '세종', '충북', '충남'] },
      { id: 'yeongnam', label: '영남권', regions: ['부산', '대구', '울산', '경북', '경남'] },
      { id: 'honam', label: '호남권', regions: ['광주', '전북', '전남'] },
      { id: 'others', label: '강원/제주', regions: ['강원', '제주'] }
    ]

    const allItems = ref([])

    const loadData = async () => {
      isLoading.value = true
      await fetchProperties()

      allItems.value = properties.value.map(item => ({
        id: item.id,
        region: item.regionMain || '기타',
        location: item.regionSub || '지역미정',
        type: item.type || '아파트',
        title: item.name,
        downPaymentHighlight: item.initialFund || '상담문의',
        downPaymentSub: item.middlePayment || ''
      }))

      isLoading.value = false
    }

    onMounted(async () => {
      await loadData()
      setTimeout(() => {
        checkScroll()
        window.addEventListener('resize', checkScroll)
      }, 100)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', checkScroll)
    })

    // 선택된 권역의 세부 지역 목록
    const currentAreaRegions = computed(() => {
      const area = areas.find(a => a.id === selectedArea.value)
      return area ? area.regions : []
    })

    // 필터된 현장 목록 (선택된 지역 기준)
    const filteredItems = computed(() => {
      return allItems.value
        .filter(item => item.region === selectedRegion.value)
        .slice(0, 10)
    })

    // 권역 선택 시 첫 번째 지역 자동 선택
    const selectArea = (areaId) => {
      selectedArea.value = areaId
      const area = areas.find(a => a.id === areaId)
      if (area && area.regions.length > 0) {
        selectedRegion.value = area.regions[0]
      }
      // 스크롤 위치 초기화
      if (scrollContainer.value) {
        scrollContainer.value.scrollLeft = 0
      }
      setTimeout(checkScroll, 100)
    }

    // 세부 지역 선택
    const selectRegion = (region) => {
      selectedRegion.value = region
      // 스크롤 위치 초기화
      if (scrollContainer.value) {
        scrollContainer.value.scrollLeft = 0
      }
      setTimeout(checkScroll, 100)
    }

    // 지역 변경 시 스크롤 체크
    watch(selectedRegion, () => {
      setTimeout(checkScroll, 100)
    })

    const scroll = (direction) => {
      const container = scrollContainer.value
      if (!container) return
      const scrollAmount = 320
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }

    const checkScroll = () => {
      const container = scrollContainer.value
      if (!container) return
      canScrollLeft.value = container.scrollLeft > 0
      canScrollRight.value = Math.ceil(container.scrollLeft + container.clientWidth) < container.scrollWidth
    }

    const goToDetail = (id) => {
      router.push(`/properties/${id}`)
    }

    return {
      scrollContainer,
      canScrollLeft,
      canScrollRight,
      isLoading,
      selectedArea,
      selectedRegion,
      areas,
      currentAreaRegions,
      filteredItems,
      selectArea,
      selectRegion,
      scroll,
      checkScroll,
      goToDetail
    }
  }
}
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
